import * as React from 'react';
import { connect } from 'react-redux';
import { Messages } from '../../../codegen/Localize';
import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { imgPath } from '../../../common/utilities/ImgUrl';
import { IContentInfo } from '../../../ts/models/IContentInfo';
import { Views } from '../../../ts/resources/Views';
import { siteUrlsSelector } from '../../selectors/SiteUrlsSelectors';
import { ServiceLocator } from '../../ServiceLocator';
import { Thumbnail } from '../../spaces/CardView/Thumbnail';
import { AppState } from '../../state/AppState';
import { MapStateToProps } from '../../utils/ConnectUtils';
import { DetailsTooltipStyles } from '../CardView/DetailsTooltipStyles';
import { Hover } from '../Components/Hover';
import { SpacesSiteUrls } from '../SpacesSiteUrls';
import { TooltipAnchor } from '../Tooltips/TooltipAnchor';
import { isTooltipContentUnfocusable, ViewTooltip } from '../Tooltips/ViewTooltip';

export interface ConnectedViewThumbnailOwnProps {
    connectedViewId: string;
    connectedViewThumbnailUrl: string;
    connectedViewUrl: string;
}

export interface ConnectedViewThumbnailStateProps {
    urls: SpacesSiteUrls;
}

export interface ConnectedViewThumbnailAngularProps {
    readonly viewsService: Views;
}

export interface ConnectedViewThumbnailStates {
    isHovered: boolean;
    connectedViewContentInfo: IContentInfo;
}

export type ConnectedViewThumbnailProps = ConnectedViewThumbnailOwnProps & ConnectedViewThumbnailStateProps & ConnectedViewThumbnailAngularProps;

export const THUMBNAIL_WIDTH = 177;
export const THUMBNAIL_HEIGHT = 108;

export class ConnectedViewThumbnailPresentation extends React.PureComponent<ConnectedViewThumbnailProps, ConnectedViewThumbnailStates> {
    constructor(props: ConnectedViewThumbnailProps) {
        super(props);

        this.state = {
            isHovered: false,
            connectedViewContentInfo: null
        };

        this.getViewContentDetail();
    }

    public componentDidUpdate(prevProps: ConnectedViewThumbnailProps) {
        if (prevProps.connectedViewId !== this.props.connectedViewId) {
            this.getViewContentDetail();
        }
    }

    public render() {
        const disableAutoFocus = isTooltipContentUnfocusable(this.state.connectedViewContentInfo);

        return <div style={{ width: THUMBNAIL_WIDTH + 'px' }}>
            <Hover groupName="metric-detail-connected-view" onEnter={this.activateHover} onLeave={this.deactivateHover}>
                {this.state.connectedViewContentInfo ?
                    <TooltipAnchor tooltipContent={this.renderTooltipContent}
                        width={DetailsTooltipStyles.Width}
                        title={Messages.viewTooltipTitle()} disableAutoFocus={disableAutoFocus}>
                        {this.renderThumbnail()}
                    </TooltipAnchor>
                    : this.renderThumbnail()
                }
            </Hover>
        </div>;
    }

    private renderThumbnail = () => {
        return <Thumbnail navigateUrl={this.props.connectedViewUrl}
            openLinkInNewTab
            defaultImageUrl={imgPath('spaces_card_view.svg')}
            thumbnailImageUrl={this.props.connectedViewThumbnailUrl}
            hoveredOrSelected={this.state.isHovered}
            customHeight={THUMBNAIL_HEIGHT}
            allowFocusIndicator />;
    }

    private activateHover = () => {
        this.setState({ isHovered: true });
    }

    private deactivateHover = () => {
        this.setState({ isHovered: false });
    }

    private getViewContentDetail = async () => {
        if (this.props.viewsService && this.props.viewsService.getById) {
            try {
                const result = await this.props.viewsService.getById(this.props.connectedViewId);
                this.setState({ connectedViewContentInfo: { ...result } });
            } catch (e) {
                this.setState({ connectedViewContentInfo: null });
            }
        }
    }

    private renderTooltipContent = () => {
        return this.state.connectedViewContentInfo ? <ViewTooltip content={this.state.connectedViewContentInfo}
            urls={this.props.urls}
            inProjectSpace={false} /> : null;
    }
}

const mapStateToProps: MapStateToProps<ConnectedViewThumbnailStateProps> = (state: AppState) => {
    return {
        urls: siteUrlsSelector(state)
    };
};

export const ConnectedViewThumbnail = connect(mapStateToProps)(ConnectedViewThumbnailPresentation);

export namespace ConnectedViewThumbnailAngularProvider {
    function getAngularServicesWithServiceLocator() {
        const ViewService = ServiceLocator.get<Views>('Views');

        return {
            viewsService: ViewService,
        };
    }

    function selectPropsFromAngularServices(angularServices: ConnectedViewThumbnailAngularProps) {
        return angularServices;
    }

    export const HOC = angularProviderHOC<ConnectedViewThumbnailOwnProps, ConnectedViewThumbnailAngularProps, ConnectedViewThumbnailAngularProps>({
        getAngularServicesWithServiceLocator,
        selectPropsFromAngularServices
    });
}

export const ConnectedViewThumbnailAngular: React.ComponentType<ConnectedViewThumbnailOwnProps> = ConnectedViewThumbnailAngularProvider.HOC(ConnectedViewThumbnail);
